import type { Content } from 'pdfmake/interfaces';

export interface StatisticsKpi {
  labelKey: string;
  value: string;
}

/** Widget-specific PDF layout (avoids rasterizing layout that distorts charts). */
export type StatisticsPdfNativeLayout = 'emaac-alerts-sidebar';

export interface EmaacAlertsSidebarPdfData {
  totalAlerts: number;
  emaAlerts: number;
  finishedAlerts: number;
  legendLabels: [string, string];
}

export interface StatisticsExportSnapshot {
  slug: string;
  titleKey: string;
  imageDataUrl: string;
  /** Square chart raster (pie/doughnut) for native PDF layouts. */
  squareChartImageDataUrl?: string;
  pdfNativeLayout?: StatisticsPdfNativeLayout;
  emaacSidebarData?: EmaacAlertsSidebarPdfData;
  /** Render width in PDF points. Set during PDF preparation. */
  pdfWidth?: number;
  /** Render height in PDF points. Set during PDF preparation. */
  pdfHeight?: number;
  /** Optional image slices for tall widgets. */
  pdfSegments?: StatisticsPdfImageSegment[];
  /** When true, the widget image already contains a title and PDF should not repeat it. */
  omitPdfTitle?: boolean;
  /** Scale chart image up to fill the PDF content area (width and height). */
  scaleToFullPage?: boolean;
  /** Combine with other snapshots on one PDF page (same group id). */
  pdfPageGroup?: string;
  kpis?: StatisticsKpi[];
  /**
   * Native pdfmake table for this widget. When set, the PDF renders it as
   * vector text (fast, sharp, paginates automatically) instead of a raster
   * image — used for tabular widgets.
   */
  pdfNativeTable?: StatisticsPdfNativeTable;
  /**
   * Native average-alert-durations layout (stacked bar + KPI cards). When set,
   * the PDF draws it natively instead of rasterizing the widget DOM.
   */
  avgDurations?: StatisticsAvgDurationsPdfData;
}

export interface StatisticsPdfTableColumn {
  /** i18n key for the column header. */
  headerKey: string;
  align?: 'left' | 'right';
  /** pdfmake column width: number (pt), '*' to fill, or 'auto'. */
  width?: number | '*' | 'auto';
}

export interface StatisticsPdfTableRow {
  /** Pre-formatted cell text, one per column. */
  cells: string[];
  /** Bold group-header row (e.g. a device-type subtotal). */
  group?: boolean;
  /** Indent the first cell (a child item under a group header). */
  indent?: boolean;
}

export interface StatisticsPdfNativeTable {
  /** Large emphasised value shown under the title (e.g. total alerts). */
  headlineValue?: string;
  columns: StatisticsPdfTableColumn[];
  rows: StatisticsPdfTableRow[];
  /**
   * Render the rows across this many side-by-side column groups (column-major,
   * like a CSS multi-column list) to save vertical space. Default 1.
   */
  columnGroups?: number;
}

/** One coloured segment of the average-alert-durations stacked bar / KPI cards. */
export interface StatisticsAvgDurationSegment {
  label: string;
  /** Pre-formatted value (e.g. "1,44 s"). */
  value: string;
  /** Raw seconds — used for the stacked bar proportions. */
  seconds: number;
  /** Hex fill colour. */
  color: string;
}

export interface StatisticsAvgDurationsPdfData {
  totalLabel: string;
  totalValue: string;
  segments: StatisticsAvgDurationSegment[];
}

/** Export target: full PDF (prefers native rendering) vs single-widget PNG (needs a raster image). */
export type StatisticsExportMode = 'pdf' | 'png';

/** Alarm center sidebar: care recipients + response time (left), alerts pie (right). */
export const ALARM_CENTER_SIDEBAR_PDF_GROUP = 'alarm-center-sidebar';

export interface StatisticsPdfImageSegment {
  imageDataUrl: string;
  pdfWidth: number;
  pdfHeight: number;
}

export type StatisticsPdfWidgetLayout = 'single' | 'two-column';

export interface StatisticsExportWidget {
  captureForExport(mode?: StatisticsExportMode): Promise<StatisticsExportSnapshot | null>;
  restoreAfterExport?(): void;
}

export type StatisticsTabKey = 'alerts' | 'alarmCenter' | 'careRecipients' | 'activeUsers';

export interface StatisticsFilterLine {
  label: string;
  value: string;
}

export interface StatisticsPdfExportContext {
  tab: StatisticsTabKey;
  dateOptionText: string;
  startDate: Date;
  endDate: Date;
  filterLines: StatisticsFilterLine[];
  snapshots: StatisticsExportSnapshot[];
  kpis: StatisticsKpi[];
}

export type StatisticsExportProgressPhase = 'capture' | 'prepare' | 'generate';

export interface StatisticsExportProgress {
  phase: StatisticsExportProgressPhase;
  current: number;
  total: number;
  /** i18n key for the status line (often a widget titleKey). */
  messageKey?: string;
}

export type StatisticsExportProgressCallback = (progress: StatisticsExportProgress) => void;

export interface StatisticsTabExportSource {
  collectExportSnapshots(onProgress?: StatisticsExportProgressCallback): Promise<StatisticsExportSnapshot[]>;
  getExportKpis(): StatisticsKpi[];
  /** Re-layout charts/widgets after PDF capture (optional). */
  restoreViewAfterExport?(): void;
}

export type StatisticsPdfTitleContent = Content[];
